// Persona-switch recovery (PRD #156).
//
// Staff persona switching is optimistic from the UI's perspective: the
// shell reflects the selected persona immediately while the server-side
// switch + audit write (ADR-0004) complete in the background. If the
// server denies the switch (the user lost the grant, or the audit write
// fails) the shell must recover to a persona it is allowed to act as,
// rather than sit on a view the server will gate every read against.
//
// `resolvePersonaFallback` is the pure recovery rule, kept separate so the
// success / failure / unauthorised paths are unit-testable without a DOM.

import { type FrontendKey, type Persona, IM_ASSISTANT_TOKEN } from '@septima/contract';

/**
 * The active persona a given shell should run as for this user (ADR-0013).
 * Identity for the four frontends; but on the Desk shell an `im-assistant`-only
 * user (granted `im-assistant`, not `desk`) runs as `im-assistant` — so the
 * layout never tries to switch them INTO the ungranted `desk` persona.
 */
export function intendedPersonaForFrontend(
    frontend: FrontendKey,
    grantedTokens: readonly string[],
): Persona {
    if (
        frontend === 'desk' &&
        !grantedTokens.includes('desk') &&
        grantedTokens.includes(IM_ASSISTANT_TOKEN)
    ) {
        return IM_ASSISTANT_TOKEN;
    }
    return frontend;
}

/**
 * Choose the persona to fall back to after a denied / ungranted switch to
 * `attempted`. Prefers the previously-active persona when it is still
 * granted and is not the one that just failed; otherwise the first granted
 * persona that is not `attempted`. Returns `null` when there is no other
 * valid persona to recover to (the shell then stays put).
 */
export function resolvePersonaFallback(
    previousPersona: string | null,
    granted: readonly FrontendKey[],
    attempted: FrontendKey,
): FrontendKey | null {
    if (
        previousPersona &&
        previousPersona !== attempted &&
        (granted as readonly string[]).includes(previousPersona)
    ) {
        return previousPersona as FrontendKey;
    }
    return granted.find((g) => g !== attempted) ?? null;
}
